import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, type Shortlink } from "@/lib/api";
import { canEdit, isAdmin, useAuth } from "@/lib/auth";
import AyudaVista from "@/ui/AyudaVista";
import Modal from "@/ui/Modal";
import { ayudaEnlaces } from "@/components/ayuda/vistas/enlaces";
import { useToast } from "@/ui/Toast";
import { copiarTexto } from "@/lib/copiar";

/**
 * Acortador de URLs internas (r.arknex.net). El agente pega una URL
 * larga (Drive, Notion, doc externo) y obtiene una URL chica para
 * mandar al cliente.
 *
 * UX:
 * - Form sticky arriba con URL + slug opcional.
 * - Si la URL ya tiene shortlink, el backend devuelve 409 con el
 *   existente; aqui mostramos "ya existe — reusar?" y un click reusa.
 * - Lista con preview meta (titulo destino + favicon) + analytics
 *   simples (visitas + ultima abierta).
 * - Acciones por row: copiar, ver QR, editar destino, borrar.
 */
export default function Shortlinks() {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const [items, setItems] = useState<Shortlink[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [url, setUrl] = useState("");
  const [slugCustom, setSlugCustom] = useState("");
  // Si el backend dice "duplicado, ya existe", lo mostramos en lugar
  // de crear ciego. El user decide si reusar o cancelar.
  const [duplicado, setDuplicado] = useState<Shortlink | null>(null);
  const [editando, setEditando] = useState<Shortlink | null>(null);
  const [editUrl, setEditUrl] = useState("");
  const [qrAbierto, setQrAbierto] = useState<Shortlink | null>(null);
  const [confirmBorrar, setConfirmBorrar] = useState<Shortlink | null>(null);
  const urlRef = useRef<HTMLInputElement>(null);

  async function cargar() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      const path = `/api/shortlinks${params.toString() ? `?${params}` : ""}`;
      setItems(await api.get<Shortlink[]>(path));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar los enlaces cortos", msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => void cargar(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Ctrl/Cmd+K enfoca el input de URL para que crear sea siempre rapido.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        urlRef.current?.focus();
        urlRef.current?.select();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  async function crear(reusar = false) {
    const u = url.trim();
    if (!u) return;
    setSubmitting(true);
    try {
      const body: { url: string; slug?: string; reusar_si_existe?: boolean } = {
        url: u,
      };
      if (slugCustom.trim()) body.slug = slugCustom.trim();
      if (reusar) body.reusar_si_existe = true;
      const created = await api.post<Shortlink>("/api/shortlinks", body);
      setUrl("");
      setSlugCustom("");
      setDuplicado(null);
      toast.success(reusar ? "Reutilizado" : "Creado", created.short_url);
      // Copiar al clipboard al crear — el agente acaba de generar el link
      // y casi seguro lo va a pegar en seguida en un correo o chat.
      void copiar(created.short_url, /*silencioso=*/ true);
      await cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Intentar parsear el "existente" del JSON del backend.
        try {
          const data = JSON.parse(err.detail) as {
            existente?: Shortlink;
            detalle?: string;
          };
          if (data.existente) {
            setDuplicado(data.existente);
            setSubmitting(false);
            return;
          }
        } catch {
          // detalle no es JSON, cae al toast generico abajo
        }
      }
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude crear el enlace corto", msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function copiar(text: string, silencioso = false) {
    if (await copiarTexto(text)) {
      if (!silencioso) toast.success("Copiado al portapapeles", text);
    } else {
      toast.error("No pude copiar — selecciona el texto a mano");
    }
  }

  async function guardarEdit() {
    if (!editando) return;
    const u = editUrl.trim();
    if (!u) return;
    try {
      const updated = await api.patch<Shortlink>(
        `/api/shortlinks/${editando.id}`,
        { url: u },
      );
      setEditando(null);
      toast.success("Destino actualizado", updated.short_url);
      await cargar();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude actualizar", msg);
    }
  }

  async function borrar(sl: Shortlink) {
    try {
      await api.del(`/api/shortlinks/${sl.id}`);
      toast.success("Enlace corto eliminado", sl.short_url);
      setConfirmBorrar(null);
      await cargar();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude borrar", msg);
    }
  }

  function onKeyDownForm(e: React.KeyboardEvent<HTMLInputElement>) {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      if (url.trim() && !submitting) void crear();
    }
  }

  // Total visitas + total links — stats chiquitas arriba.
  const totalVisitas = useMemo(
    () => items.reduce((a, s) => a + s.visits, 0),
    [items],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-2">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-xl font-semibold">Enlaces cortos</h1>
            <p className="text-sm text-zinc-400">
              Acorta URLs largas para mandar al cliente en{" "}
              <code className="text-accent-400">r.arknex.net/aB3xK9</code>
            </p>
          </div>
          <AyudaVista titulo="Enlaces cortos" {...ayudaEnlaces({ veTodos: canEdit(me) })} />
        </div>
        <div className="text-xs text-zinc-500">
          {items.length} enlace{items.length === 1 ? "" : "s"} ·{" "}
          {totalVisitas} visita{totalVisitas === 1 ? "" : "s"} totales
        </div>
      </div>

      {/* Form sticky arriba */}
      <div className="card sticky top-2 z-10 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-2">
          <input
            ref={urlRef}
            type="url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (duplicado) setDuplicado(null);
            }}
            onKeyDown={onKeyDownForm}
            placeholder="https://drive.google.com/... (Ctrl+K enfoca aquí)"
            className="input"
          />
          <input
            type="text"
            value={slugCustom}
            onChange={(e) => setSlugCustom(e.target.value)}
            onKeyDown={onKeyDownForm}
            placeholder="Terminación personalizada (opcional)"
            className="input"
          />
          <button
            onClick={() => void crear()}
            disabled={submitting || !url.trim()}
            className="btn-primary disabled:opacity-50 whitespace-nowrap"
          >
            {submitting ? "Creando…" : "Acortar"}
          </button>
        </div>
        {duplicado && (
          <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
            <p className="text-amber-200 mb-1">
              Esa URL ya tiene un enlace corto:{" "}
              <strong className="text-amber-100">{duplicado.short_url}</strong>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => void crear(true)}
                disabled={submitting}
                className="btn-primary text-xs"
              >
                Reusar el existente
              </button>
              <button
                onClick={() => setDuplicado(null)}
                className="btn-ghost text-xs"
              >
                Cancelar
              </button>
              <button
                onClick={() => void copiar(duplicado.short_url)}
                className="btn-ghost text-xs"
              >
                Copiar
              </button>
            </div>
          </div>
        )}
      </div>

      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por título, terminación o URL…"
        className="input w-full"
      />

      {loading ? (
        <p className="text-sm text-zinc-500">Cargando…</p>
      ) : items.length === 0 ? (
        <div className="card py-8 text-center">
          <p className="text-sm text-zinc-500">
            {q.trim() ? "Ningún enlace corto coincide." : "Aún no tienes enlaces cortos. Pega una URL arriba para crear el primero."}
          </p>
        </div>
      ) : (
        <div className="card divide-y divide-ink-700 p-0">
          {items.map((sl) => (
            <ShortlinkRow
              key={sl.id}
              sl={sl}
              // El servidor deja editar y borrar al creador o a un admin; la
              // pantalla solo se lo enseñaba al creador (2026-10-13).
              puedeCambiar={sl.created_by_id === me?.id || isAdmin(me)}
              onCopiar={() => void copiar(sl.short_url)}
              onQr={() => setQrAbierto(sl)}
              onEditar={() => {
                setEditando(sl);
                setEditUrl(sl.url);
              }}
              onBorrar={() => setConfirmBorrar(sl)}
            />
          ))}
        </div>
      )}

      {/* Modal: ver QR grande + descargar PNG */}
      {qrAbierto && (
        <Modal
          open={true}
          onOpenChange={(v) => !v && setQrAbierto(null)}
          title="Código QR"
          description={qrAbierto.short_url}
          size="sm"
          footer={
            <>
              <button
                onClick={() => setQrAbierto(null)}
                className="btn-ghost"
              >
                Cerrar
              </button>
              <a
                href={`/api/shortlinks/${qrAbierto.id}/qr.png`}
                download={`${qrAbierto.slug}.png`}
                className="btn-primary"
              >
                ↓ Descargar PNG
              </a>
            </>
          }
        >
          <div className="flex items-center justify-center bg-white rounded p-3">
            <img
              src={`/api/shortlinks/${qrAbierto.id}/qr.png`}
              alt={`QR de ${qrAbierto.short_url}`}
              className="w-64 h-64"
            />
          </div>
        </Modal>
      )}

      {/* Modal: editar destino */}
      {editando && (
        <Modal
          open={true}
          onOpenChange={(v) => !v && setEditando(null)}
          title="Editar destino"
          description={`La terminación ${editando.slug} no cambia — los enlaces ya repartidos siguen funcionando.`}
          size="md"
          footer={
            <>
              <button onClick={() => setEditando(null)} className="btn-ghost">
                Cancelar
              </button>
              <button
                onClick={() => void guardarEdit()}
                disabled={!editUrl.trim() || editUrl === editando.url}
                className="btn-primary disabled:opacity-50"
              >
                Guardar
              </button>
            </>
          }
        >
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Nuevo destino
          </label>
          <input
            type="url"
            value={editUrl}
            onChange={(e) => setEditUrl(e.target.value)}
            className="input mt-1 w-full"
            autoFocus
          />
        </Modal>
      )}

      {/* Confirm borrar */}
      {confirmBorrar && (
        <Modal
          open={true}
          onOpenChange={(v) => !v && setConfirmBorrar(null)}
          title="Eliminar enlace corto"
          description={`El enlace ${confirmBorrar.short_url} dejará de funcionar inmediatamente. Si lo abren después, verán un error de página no encontrada.`}
          size="sm"
          footer={
            <>
              <button
                onClick={() => setConfirmBorrar(null)}
                className="btn-ghost"
              >
                Cancelar
              </button>
              <button
                onClick={() => void borrar(confirmBorrar)}
                className="btn bg-red-600 hover:bg-red-500 text-white"
              >
                Eliminar
              </button>
            </>
          }
        >
          <p className="text-sm text-zinc-300">
            Destino: <span className="text-zinc-500 break-all">{confirmBorrar.url}</span>
          </p>
        </Modal>
      )}
    </div>
  );
}

/** Fila de un shortlink — favicon + título + slug + URL destino + stats
 * + acciones inline. Una sola fila por item, layout responsive. */
function ShortlinkRow({
  sl,
  puedeCambiar,
  onCopiar,
  onQr,
  onEditar,
  onBorrar,
}: {
  sl: Shortlink;
  puedeCambiar: boolean;
  onCopiar: () => void;
  onQr: () => void;
  onEditar: () => void;
  onBorrar: () => void;
}) {
  const hace = relTime(sl.last_visit_at);
  return (
    <div className="flex flex-col gap-2 px-4 py-3 hover:bg-ink-800/30 transition sm:flex-row sm:items-center sm:gap-3">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        {/* Favicon o placeholder */}
        <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center mt-0.5">
          {sl.favicon_url ? (
            <img
              src={sl.favicon_url}
              alt=""
              className="w-5 h-5 rounded"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <span className="text-zinc-600 text-xs">·</span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={sl.short_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-mono text-accent-400 hover:text-accent-300 break-all"
            >
              {sl.short_url.replace(/^https?:\/\//, "")}
            </a>
            {sl.titulo_destino && (
              <span className="text-sm text-zinc-200 truncate">
                {sl.titulo_destino}
              </span>
            )}
          </div>
          {/* El destino completo, partido si es largo (antes cortado y entero
              solo en un tooltip). */}
          <p className="text-xs text-zinc-500 mt-0.5 break-all">
            → {sl.url}
          </p>
          <p className="text-[10px] text-zinc-600 mt-0.5">
            {sl.visits} visita{sl.visits === 1 ? "" : "s"}
            {hace && ` · última hace ${hace}`}
            {sl.created_by_nombre && ` · creado por ${sl.created_by_nombre}`}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap text-xs sm:flex-shrink-0 sm:justify-end">
        <button onClick={onCopiar} className="btn-ghost">
          copiar
        </button>
        <button onClick={onQr} className="btn-ghost">
          QR
        </button>
        {puedeCambiar && (
          <>
            <button onClick={onEditar} className="btn-ghost">
              editar
            </button>
            <button
              onClick={onBorrar}
              className="btn-ghost text-red-400/80 hover:text-red-300"
            >
              eliminar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** "hace X" para timestamps recientes. Devuelve null si no hay fecha. */
function relTime(iso: string | null): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const seg = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (seg < 60) return `${seg}s`;
  const min = Math.round(seg / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const dias = Math.round(hr / 24);
  if (dias < 30) return `${dias}d`;
  const meses = Math.round(dias / 30);
  if (meses < 12) return `${meses} mes${meses === 1 ? "" : "es"}`;
  return `${Math.round(meses / 12)}a`;
}
